import type { AppSchemasCipCodeCIPCode } from "@classes-service-types/app-schemas-cip-code-cipcode";
import type { CountryWithEmergencyContact } from "@classes-service-types/country-with-emergency-contact";
import type { LearnerType } from "@classes-service-types/learner-type";
import type { RequiredLanguage } from "@classes-service-types/required-language";
import { useQuery } from "@tanstack/vue-query";
import { computed } from "vue";
import cipCodesService from "@/services/cipCodes";
import countriesService from "@/services/countries";
import languagesService from "@/services/languages";
import learnerTypesServices from "@/services/learnerTypes";

type LanguageOption = RequiredLanguage & {
  label: string;
};

export const useInitialOptionsData = () => {
  const { data: countries, isLoading: isCountriesLoading } = useQuery({
    enabled: true,
    queryKey: ["initial-countries"],
    queryFn: async (): Promise<CountryWithEmergencyContact[]> => {
      const response = await countriesService.getCountries(20, 1, "");
      return response?.data.data ?? [];
    },
  });

  const { data: learnerTypes, isLoading: isLearnerTypesLoading } = useQuery({
    enabled: true,
    queryKey: ["initial-learner-types"],
    queryFn: async (): Promise<LearnerType[]> => {
      const response = await learnerTypesServices.list({ limit: 10 });
      return response.items;
    },
  });

  const { data: cipCodes, isLoading: isCipCodesLoading } = useQuery({
    enabled: true,
    queryKey: ["initial-cip-codes"],
    queryFn: async (): Promise<AppSchemasCipCodeCIPCode[]> => {
      const response = await cipCodesService.list();
      return response;
    },
  });

  const { data: languages, isLoading: isLanguagesLoading } = useQuery({
    enabled: true,
    queryKey: ["initial-languages"],
    queryFn: async (): Promise<LanguageOption[]> => {
      const response: RequiredLanguage[] = await languagesService.list(100);
      return response.map((language) => {
        return {
          label: language.value,
          ...language,
        };
      });
    },
  });

  const isLoading = (): boolean => {
    return (
      isCountriesLoading.value || isLearnerTypesLoading.value || isCipCodesLoading.value || isLanguagesLoading.value
    );
  };

  const initialData = computed(() => {
    return {
      countries: countries.value,
      learnerTypes: learnerTypes.value,
      cipCodes: cipCodes.value,
      languages: languages.value,
    };
  });

  const learnerType = (learnerTypeId: string): LearnerType | undefined => {
    return learnerTypes.value?.find((each) => each.id === learnerTypeId);
  };

  return {
    initialData,
    isLoading,
    learnerType,
    languages,
  };
};
